import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getEtsyConfigSets, deleteEtsyConfigSet, createEtsyConfigSet } from '@/lib/queries/configSets'
import { getEtsyHealth } from '@/lib/etsy/health'
import { disconnectEtsy } from '@/lib/etsy/disconnect'
import toast from 'react-hot-toast'

/**
 * Etsy Config Sets List page
 * Shows all Etsy config sets with view/edit/delete actions
 */
export default function EtsyConfigSetsList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const { brandId } = useParams<{ brandId?: string }>()
  const etsyBasePath = brandId ? `/brands/${brandId}/integrations/etsy` : '/integrations/etsy'

  const frontendOrigin =
    typeof window !== 'undefined' && typeof window.location?.origin === 'string' ? window.location.origin : ''
  const frontendOriginParam = frontendOrigin ? `&frontendOrigin=${encodeURIComponent(frontendOrigin)}` : ''
  const oauthRedirectPath = encodeURIComponent(etsyBasePath)
  const etsyConnectHref = `/api/etsy/oauth/start?redirect=${oauthRedirectPath}${
    brandId ? `&brandId=${encodeURIComponent(brandId)}` : ''
  }${frontendOriginParam}&forceLogin=1`

  const { data: etsyHealth, isLoading: isEtsyHealthLoading } = useQuery({
    queryKey: ['etsy', 'health', brandId || 'legacy'],
    queryFn: () => getEtsyHealth(brandId),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectEtsy(brandId),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['etsy', 'shops', brandId || 'legacy'] })
      queryClient.setQueryData(['etsy', 'health', brandId || 'legacy'], {
        connected: false,
        provider: 'etsy',
        reason: 'missing_refresh_token',
      })
      await queryClient.refetchQueries({ queryKey: ['etsy', 'health', brandId || 'legacy'] })
      toast.success('Etsy disconnected')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect Etsy')
    },
  })

  const { data: configSets, isLoading } = useQuery({
    queryKey: ['etsyConfigSets', brandId || 'all'],
    queryFn: () => getEtsyConfigSets(brandId),
    // Always fetch fresh data, no caching
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
    // Don't use cached data
    placeholderData: undefined,
  })

  const { mutateAsync: doDelete } = useMutation({
    mutationFn: deleteEtsyConfigSet,
    onSuccess: async (_, deletedId) => {
      toast.success('Config set deleted successfully')
      // Remove from deleting set immediately
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(deletedId)
        return next
      })
      // Remove the deleted config set from cache
      queryClient.removeQueries({ queryKey: ['etsyConfigSet', deletedId] })
      queryClient.removeQueries({ queryKey: ['productsByConfigSet', deletedId] })
      // Remove list from cache and refetch fresh data
      queryClient.removeQueries({ queryKey: ['etsyConfigSets', brandId || 'all'] })
      await queryClient.refetchQueries({ queryKey: ['etsyConfigSets', brandId || 'all'] })
    },
    onError: (error, deletedId) => {
      // Remove from deleting set on error
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(deletedId)
        return next
      })
      throw error // Re-throw so handleDelete can catch it
    },
  })

  const { mutateAsync: doCreate, isPending: isCreating } = useMutation({
    mutationFn: () => createEtsyConfigSet(brandId),
    onSuccess: async (data) => {
      // Remove from cache and refetch fresh data
      queryClient.removeQueries({ queryKey: ['etsyConfigSets', brandId || 'all'] })
      await queryClient.refetchQueries({ queryKey: ['etsyConfigSets', brandId || 'all'] })
      navigate(`${etsyBasePath}/${data._id}/edit`)
    },
  })

  const handleCreate = async () => {
    try {
      await doCreate()
    } catch (error) {
      toast.error(`Failed to create config set: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const [showMenuId, setShowMenuId] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())

  const handleDelete = async (id: string, name: string | undefined, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }
    setShowMenuId(null)
    const displayName = name || 'this config set'
    const ok = window.confirm(`Are you sure you want to delete "${displayName}"? This cannot be undone.`)
    if (!ok) return
    
    // Grey out the item immediately
    setDeletingIds((prev) => new Set(prev).add(id))
    
    try {
      await doDelete(id)
      // Item will be removed from list when query refetches
      // deletingIds is cleared in onSuccess callback
    } catch (error) {
      // Error handling is done in onError callback, but show toast here too
      console.error('Delete failed:', error)
      toast.error(`Failed to delete config set: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Filter config sets based on search query
  const filteredConfigSets = useMemo(() => {
    if (!configSets) return []

    if (!searchQuery.trim()) return configSets

    const query = searchQuery.toLowerCase().trim()
    return configSets.filter((configSet) => {
      const nameMatch = configSet.name?.toLowerCase().includes(query)
      const descMatch = configSet.description?.toLowerCase().includes(query)
      return nameMatch || descMatch
    })
  }, [configSets, searchQuery])

  // Helper to format date (UK format: DD MM YYYY)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    } catch {
      return 'N/A'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="p-4">
            <h1 className="text-2xl font-bold text-gray-900">Etsy Config Sets</h1>
          </div>
        </header>
        <main className="p-4">
          <div className="text-center py-8 text-gray-600">Loading config sets...</div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            Etsy Config Sets ({configSets?.length ?? 0})
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {!isEtsyHealthLoading && etsyHealth?.connected && (
          <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium text-emerald-800">Etsy connected</span>
              <span className="text-gray-600"> — disconnect to remove stored tokens for this {brandId ? 'brand' : 'workspace'}.</span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    'Disconnect Etsy? Local tokens will be removed; you must connect again to import listings.',
                  )
                ) {
                  disconnectMutation.mutate()
                }
              }}
              disabled={disconnectMutation.isPending}
              className="shrink-0 px-4 py-3 rounded-lg border border-red-300 text-red-700 font-medium text-sm touch-target-min-44 hover:bg-red-50 disabled:opacity-50"
            >
              {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect Etsy'}
            </button>
          </div>
        )}
        {!isEtsyHealthLoading && !etsyHealth?.connected && (
          <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium text-red-800">Etsy not connected</span>
              <span className="text-gray-600"> — connect to authorize this app for Etsy API access.</span>
            </div>
            <a
              href={etsyConnectHref}
              className="shrink-0 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-emerald-600 text-white font-medium text-sm touch-target-min-44 hover:bg-emerald-700 text-center"
            >
              Connect Etsy
            </a>
          </div>
        )}
        {/* Search */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search config sets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
          />
        </div>

        {/* Config Sets List */}
        {filteredConfigSets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600 mb-2">
              {searchQuery ? 'No config sets found matching your search.' : 'No config sets found.'}
            </p>
            {!searchQuery && (
              <Link
                to={`${etsyBasePath}/new`}
                className="text-blue-600 hover:underline font-medium"
              >
                Create your first config set
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConfigSets.map((configSet) => {
              const isDeleting = deletingIds.has(configSet._id)
              return (
                <div
                  key={configSet._id}
                  className={`bg-white border border-gray-200 rounded-lg p-4 transition-all touch-target-min-44 ${
                    isDeleting
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-md cursor-pointer'
                  }`}
                  onClick={() => !isDeleting && navigate(`${etsyBasePath}/${configSet._id}`)}
                >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate mb-1">
                      {configSet.name || 'Untitled Config Set'}
                    </h3>
                    {configSet.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                        {configSet.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>Created: {formatDate(configSet._createdAt)}</span>
                      {configSet.productCount !== undefined && (
                        <span className="font-medium">
                          {configSet.productCount === 0
                            ? 'No products'
                            : configSet.productCount === 1
                              ? '1 product'
                              : `${configSet.productCount} products`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowMenuId(showMenuId === configSet._id ? null : configSet._id)
                      }}
                      disabled={isDeleting}
                      className="w-11 h-11 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="More options"
                    >
                      ⋯
                    </button>
                    {showMenuId === configSet._id && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowMenuId(null)
                          }}
                        />
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(configSet._id, configSet.name, e)
                            }}
                            disabled={isDeleting}
                            className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors touch-target-min-44 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isDeleting ? 'Deleting...' : 'Delete Config Set'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Floating Action Button */}
      <button
        onClick={handleCreate}
        disabled={isCreating}
        className="fixed bottom-24 right-5 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-light shadow-lg hover:bg-blue-700 transition-colors touch-target-min-44 z-50 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Create new config set"
      >
        {isCreating ? '...' : '+'}
      </button>
    </div>
  )
}
